import React from "react";
import { Button as BaseButton } from "@base-ui/react/button";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
type ButtonSize = "sm" | "md" | "icon";

type ButtonProps = Omit<React.ComponentPropsWithoutRef<typeof BaseButton>, "className"> & {
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
};

const variants: Record<ButtonVariant, string> = {
  primary:
    "border-neutral-950 bg-neutral-950 text-white shadow-[0_1px_2px_rgba(0,0,0,0.18),0_8px_24px_rgba(0,0,0,0.08)] hover:border-neutral-800 hover:bg-neutral-800",
  secondary: "border-neutral-200 bg-white text-neutral-900 shadow-[0_1px_1px_rgba(0,0,0,0.04)] hover:border-neutral-300 hover:bg-neutral-50",
  danger: "border-red-200 bg-white text-red-700 shadow-[0_1px_1px_rgba(0,0,0,0.04)] hover:border-red-300 hover:bg-red-50",
  ghost: "border-transparent bg-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950",
};

const sizes: Record<ButtonSize, string> = {
  sm: "min-h-9 gap-2 px-3 text-sm",
  md: "min-h-10 gap-2 px-4 text-sm",
  icon: "h-10 w-10 justify-center p-0",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "secondary", size = "md", ...props }, ref) => (
    <BaseButton
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded-md border font-medium outline-none transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out",
        "focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
        "active:translate-y-px disabled:pointer-events-none disabled:opacity-50 touch-manipulation",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  ),
);

Button.displayName = "Button";
